/**
 * Tile assembly — build the sentence from its own words (TASKS T50, SPEC §5 tiers 2 and 5).
 *
 * The tiles are **exactly the words of the target sentence**, shuffled, and all of them are used.
 * There are no distractor tiles, and there is no set of candidate answers to choose between: adding
 * either would turn word order into a selection task, and invariant 3 forbids multiple choice as a
 * scored mode anywhere. What is being retrieved here is order and lexis, not a preference between
 * options someone else wrote.
 *
 * The module is in two halves, and they are here together because they are one subject.
 *
 *  1. **The pure half** — cutting a sentence into tiles, and the placement reducer. No DOM, no
 *     clock, no store; the shuffle takes an injected `Rng` so the same seed lays the same tiles out
 *     twice (DoD-14).
 *  2. **The widget** — the bank and the assembled line, as real buttons. Buttons because a tile has
 *     to be reachable three ways: by thumb, by Tab, and by a gamepad D-pad (T77). A `div` with a
 *     click handler is reachable by exactly one of those.
 */

import type { Rng } from '../core/rng';
import { button, el } from '../ui/shell';
import type { ModeInput, Presentation } from './types';

// ---------------------------------------------------------------------------------------------
// Cutting the sentence up
// ---------------------------------------------------------------------------------------------

/**
 * Split a sentence into tile faces.
 *
 * Punctuation is stripped rather than carried on a tile, because `¿` and `?` are position markers:
 * a tile reading `¿Tú` announces itself as the first one and a tile reading `descansar?` as the
 * last, which hands over both ends of the answer before the learner has thought about any of it.
 * Scoring ignores punctuation anyway (`normalizeForScoring`), so nothing is lost by leaving it off.
 *
 * Capitalisation is left exactly as the sentence has it. It does leak which tile starts the
 * sentence. The alternative — lower-casing the first tile — silently mangles a proper noun the day
 * one enters the corpus, and a card that renders a name wrongly is worse than one that is slightly
 * easier. See DECISIONS D-117.
 */
export function tilesFor(sentence: string): string[] {
  return sentence
    .split(/\s+/)
    .map((word) => word.replace(/^[¿¡"'(]+|[.,;:!?"')]+$/g, ''))
    .filter((word) => word.length > 0);
}

/**
 * Lay the tiles out for the learner.
 *
 * Shuffled, and then checked: a shuffle that happens to return the sentence in order is a card that
 * asks nothing, and over a few hundred draws of a four-word sentence it will happen. Re-shuffled up
 * to a few times, then left alone — a sentence whose words are all identical has no other order to
 * find, and looping forever over that would hang the run rather than serve an easy card.
 */
export function shuffleTiles(tiles: readonly string[], rng: Rng, attempts = 8): string[] {
  if (tiles.length < 2) return [...tiles];
  let out = rng.shuffle(tiles);
  for (let i = 0; i < attempts && out.every((tile, index) => tile === tiles[index]); i += 1) {
    out = rng.shuffle(tiles);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Placement
// ---------------------------------------------------------------------------------------------

/**
 * Which tiles have been placed, in the order they were placed.
 *
 * Indices into the laid-out bank rather than the strings themselves, because a sentence can use the
 * same word twice (`de` in `un vaso de agua de la mesa`) and two tiles reading `de` are different
 * tiles. Tracking strings would make removing one of them ambiguous.
 */
export type Placement = readonly number[];

export function placeTile(placed: Placement, index: number): number[] {
  return placed.includes(index) ? [...placed] : [...placed, index];
}

/** Take one tile back out of the line. Returns it to the bank in its original position. */
export function removeTile(placed: Placement, index: number): number[] {
  return placed.filter((entry) => entry !== index);
}

/** Undo: take back the last tile placed. Never clears the line — B is undo, not reset. */
export function undoTile(placed: Placement): number[] {
  return placed.slice(0, -1);
}

/** The answer as a string, for the validator. Plain spaces; scoring ignores punctuation. */
export function assembled(tiles: readonly string[], placed: Placement): string {
  return placed
    .map((index) => tiles[index] ?? '')
    .filter((face) => face.length > 0)
    .join(' ');
}

/** Tiles still in the bank, in their laid-out order. */
export function remaining(tiles: readonly string[], placed: Placement): number[] {
  return tiles.map((_, index) => index).filter((index) => !placed.includes(index));
}

// ---------------------------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------------------------

/**
 * Build the tile card.
 *
 * Tier 2 assembles the item's carrier sentence; tier 5 assembles the phrase itself. Either way the
 * graded string is the **whole sentence**, not the item's headword — that is what the card asked
 * for, and D-114 says the two must be the same string.
 *
 * With nothing to cut up — an item with no carrier sentence and a single-word `es` — the card falls
 * back to a typed translation rather than showing one tile and calling it a task. The item is due
 * either way and invariant 7 says a review is never withheld.
 */
export function tilesPresentation(input: ModeInput): Presentation {
  const { item, extras } = input;
  const target = item.tier === 2 ? (extras.example?.es ?? item.es) : item.es;
  const gloss = item.tier === 2 ? (extras.example?.en ?? item.en) : item.en;
  const faces = tilesFor(target);

  if (faces.length < 2 || !input.rng) {
    return {
      mode: 'tiles',
      instruction: 'Say it in Spanish',
      segments: [{ kind: 'text', text: gloss }],
      gloss: null,
      expected: target,
      scaffold: null,
      input: 'text',
      fieldLabel: 'Your answer',
      announce: `${gloss}. Type it in Spanish.`,
    };
  }

  return {
    mode: 'tiles',
    instruction: 'Put the words in order',
    // The English is the prompt here: the Spanish is what the learner is being asked to build, and
    // showing it would leave nothing to assemble.
    segments: [{ kind: 'text', text: gloss }],
    gloss: null,
    expected: target,
    // The first word is a real hint on a card whose whole task is order, and asking for it marks the
    // attempt hinted (invariant 2, T54).
    scaffold: `Starts with "${faces[0]!}".`,
    input: 'tiles',
    tiles: shuffleTiles(faces, input.rng),
    fieldLabel: 'Your answer',
    announce: `${gloss}. Put the Spanish words in order.`,
  };
}

// ---------------------------------------------------------------------------------------------
// The widget
// ---------------------------------------------------------------------------------------------

export interface TileGroupSpec {
  /** Every tile face, in laid-out order. */
  tiles: readonly string[];
  /** Indices to render in this group. */
  show: readonly number[];
  testId: string;
  label: string;
  /** Tapping a tile in this group. */
  onActivate: (index: number) => void;
  /** Rendered when `show` is empty, so the region never collapses to nothing. */
  emptyLabel?: string;
  doc?: Document;
}

/**
 * A row of tiles with roving focus.
 *
 * Roving tabindex — one tile in the tab order, arrows to move within the group — rather than every
 * tile being its own tab stop. With eight tiles the alternative is eight presses of Tab to get past
 * the bank, and the arrow keys are also what the gamepad D-pad will map onto (T77): building the
 * group around them now means the pad is a binding, not a second navigation model.
 */
export function tileGroup(spec: TileGroupSpec): HTMLElement {
  const doc = spec.doc ?? document;
  const group = el('div', 'tiles', doc);
  group.dataset['testid'] = spec.testId;
  group.setAttribute('role', 'group');
  group.setAttribute('aria-label', spec.label);

  if (spec.show.length === 0) {
    const empty = el('p', 'tiles__empty', doc);
    empty.textContent = spec.emptyLabel ?? '';
    empty.dataset['testid'] = `${spec.testId}-empty`;
    group.append(empty);
    return group;
  }

  const nodes = spec.show.map((index, position) => {
    const node = button(
      {
        label: spec.tiles[index] ?? '',
        testId: `${spec.testId}-${index}`,
        onClick: () => spec.onActivate(index),
      },
      doc,
    );
    node.classList.add('tile');
    node.lang = 'es';
    // One tab stop for the whole group; the arrows move inside it.
    node.tabIndex = position === 0 ? 0 : -1;
    return node;
  });

  group.addEventListener('keydown', (event) => {
    const delta = ARROW_DELTA[event.key];
    if (delta === undefined) return;
    const current = nodes.findIndex((node) => node === doc.activeElement);
    if (current < 0) return;
    event.preventDefault();
    // Wraps, because a thumb-driven row with a dead end makes the learner reverse direction to
    // reach the tile one step the other way.
    const next = nodes[(current + delta + nodes.length) % nodes.length]!;
    for (const node of nodes) node.tabIndex = node === next ? 0 : -1;
    next.focus();
  });

  group.append(...nodes);
  return group;
}

const ARROW_DELTA: Record<string, number | undefined> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};
